import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Mail, Phone, MapPin, Send, MessageCircle, Calendar } from 'lucide-react';

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria",
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia",
  "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo (DRC)", "Congo (Republic)",
  "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador",
  "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau",
  "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
  "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania",
  "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius",
  "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
  "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
  "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland",
  "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino",
  "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland",
  "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia",
  "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    message: '',
    phone: '',
    title: '',
    country: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Simulate submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1200);
  };

  const RequiredMark = () => <span className="text-red-500 ml-0.5">*</span>;

  return (
    <Layout>
      <SeoHead
        title="Contact EnergiPro Group | Office Locations & Inquiry"
        description="Contact EnergiPro Solutions: Al Khobar HQ (KSA), Abu Dhabi (UAE), Cairo (Egypt). Request technical proposals, RFQs, or engineering consultations."
        canonicalPath="/contact"
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white font-heading tracking-tight">
            Contact Us
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
            Reach our team directly for inquiries, partnership discussions, or operational support.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 font-heading mb-6">
                  Get in Touch
                </h2>
                <p className="text-slate-600 mb-8">
                  For technical proposals and equipment quotations, please use our{' '}
                  <a href="/request-quote" className="text-orange-600 font-semibold hover:text-orange-700 underline">
                    Request Quote
                  </a>{' '}
                  system.
                </p>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Email</h3>
                  <a
                    href="mailto:sales@energiprogroup.com"
                    className="text-orange-600 hover:text-orange-700 font-medium transition-colors"
                  >
                    sales@energiprogroup.com
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Phone</h3>
                  <a
                    href="tel:+966133308579"
                    className="text-orange-600 hover:text-orange-700 font-medium transition-colors"
                  >
                    +966 13 330 8579
                  </a>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">WhatsApp</h3>
                  <a
                    href="https://wa.me/966552693829"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Chat on WhatsApp
                  </a>
                </div>
              </div>

              {/* Schedule a Meeting */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Schedule a Meeting</h3>
                  <p className="text-slate-600 text-sm mb-2">
                    Book a 30-minute consultation with our sales team.
                  </p>
                  <a
                    href="https://calendly.com/energiprogroup-sales/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    Book a Meeting
                  </a>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Office Location</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Level 21 Al Khobar Gate Tower (Al Shaikh Tower)<br />
                    King Fahad Road<br />
                    PO Box 32348 Khobar 31952, KSA
                  </p>
                </div>
              </div>

              {/* Group Offices */}
              <div className="pt-6 border-t border-slate-200">
                <h3 className="font-bold text-slate-900 mb-4">Group Offices</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* EnergiPro Solutions — KSA */}
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">HQ — Saudi Arabia</span>
                    <h4 className="font-bold text-slate-900 mt-1">EnergiPro Solutions</h4>
                    <p className="text-sm text-slate-600 mt-1">Al Khobar, Eastern Province</p>
                    <a href="mailto:md@energiprogroup.com" className="text-sm text-orange-600 hover:text-orange-700 mt-2 block">md@energiprogroup.com</a>
                    <a href="tel:+966133308579" className="text-sm text-slate-600 hover:text-orange-600 block">+966 13 330 8579</a>
                    <a href="/" className="text-sm text-slate-600 hover:text-orange-600 block mt-1">View Profile →</a>
                  </div>
                  {/* KIMKO — UAE */}
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">UAE</span>
                    <h4 className="font-bold text-slate-900 mt-1">KIMKO Electromechanical</h4>
                    <p className="text-sm text-slate-600 mt-1">Dubai, UAE</p>
                    <a href="mailto:gm@kimkocorp.com" className="text-sm text-orange-600 hover:text-orange-700 mt-2 block">gm@kimkocorp.com</a>
                    <a href="tel:+971555412039" className="text-sm text-slate-600 hover:text-orange-600 block">+971 555 412 039</a>
                    <a href="/companies/kimko" className="text-sm text-slate-600 hover:text-orange-600 block mt-1">View Profile →</a>
                  </div>
                  {/* Petronix — Egypt */}
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Egypt</span>
                    <h4 className="font-bold text-slate-900 mt-1">Petronix Energy Services</h4>
                    <p className="text-sm text-slate-600 mt-1">Cairo, Egypt</p>
                    <a href="mailto:gm@petronix-eg.com" className="text-sm text-orange-600 hover:text-orange-700 mt-2 block">gm@petronix-eg.com</a>
                    <a href="tel:+201156969766" className="text-sm text-slate-600 hover:text-orange-600 block">+20 115 696 9766</a>
                    <a href="/companies/petronix" className="text-sm text-slate-600 hover:text-orange-600 block mt-1">View Profile →</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent</h3>
                  <p className="text-slate-600">
                    Thank you for reaching out. Our team will respond within 24 hours.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Send a Message</h3>
                  <p className="text-sm text-slate-500 mb-6">
                    Fields marked with <span className="text-red-500">*</span> are required.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                    {/* Name - Required */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                        Name <RequiredMark />
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, name: e.target.value }));
                          if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
                        }}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-slate-900 ${errors.name ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                        placeholder="Your full name"
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                    </div>

                    {/* Company Name - Required */}
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-slate-700 mb-1">
                        Company Name <RequiredMark />
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        value={formData.companyName}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, companyName: e.target.value }));
                          if (errors.companyName) setErrors(prev => ({ ...prev, companyName: '' }));
                        }}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-slate-900 ${errors.companyName ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                        placeholder="Your company name"
                      />
                      {errors.companyName && <p className="mt-1 text-xs text-red-500">{errors.companyName}</p>}
                    </div>

                    {/* Title - Optional */}
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-slate-900"
                        placeholder="Your job title"
                      />
                    </div>

                    {/* Email - Required */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                        Email <RequiredMark />
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, email: e.target.value }));
                          if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                        }}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-slate-900 ${errors.email ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                        placeholder="your@email.com"
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                    </div>

                    {/* Phone - Optional */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-slate-900"
                        placeholder="+966 XX XXX XXXX"
                      />
                    </div>

                    {/* Country - Optional */}
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-slate-700 mb-1">
                        Country
                      </label>
                      <select
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-slate-900 bg-white"
                      >
                        <option value="">Select a country</option>
                        {COUNTRIES.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message - Required */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
                        Message <RequiredMark />
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        value={formData.message}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, message: e.target.value }));
                          if (errors.message) setErrors(prev => ({ ...prev, message: '' }));
                        }}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-slate-900 resize-none ${errors.message ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                        placeholder="How can we help?"
                      />
                      {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
